import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);

  const normalizeSelectedSize = (sizeValue = '') => String(sizeValue || '').trim();

  // Helper to get user-specific key
  const getCartKey = () => user ? `cart_${user._id || user.email}` : 'cart_guest';

  // Load cart from localStorage when user changes
  useEffect(() => {
    if (user) {
      const savedCart = localStorage.getItem(getCartKey());
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      } else {
        setCart([]);
      }
    } else {
      setCart([]);
    }
    setLoading(false);
    // eslint-disable-next-line
  }, [user]);

  // Save cart to localStorage whenever it changes and user is present
  useEffect(() => {
    if (!loading && user) {
      localStorage.setItem(getCartKey(), JSON.stringify(cart));
    }
    // eslint-disable-next-line
  }, [cart, loading, user]);

  // Add item to cart
  const addToCart = (product, quantity = 1, selectedSize = '') => {
    if (!user) {
      navigate("/login?message=" + encodeURIComponent("Please sign in or register to add items to your cart."));
      return false;
    }

    const normalizedSelectedSize = normalizeSelectedSize(selectedSize);

    setCart((prevCart) => {
      // Check if product already exists in cart
      const existingItemIndex = prevCart.findIndex(
        (item) =>
          item.product._id === product._id &&
          normalizeSelectedSize(item.selectedSize) === normalizedSelectedSize
      );

      if (existingItemIndex !== -1) {
        // Update quantity if product already exists
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex].quantity += quantity;
        return updatedCart;
      } else {
        // Add new product to cart
        return [...prevCart, { product, quantity, selectedSize: normalizedSelectedSize }];
      }
    });
    return true;
  };

  // Remove item from cart
  const removeFromCart = (productId, selectedSize = '') => {
    const normalizedSelectedSize = normalizeSelectedSize(selectedSize);
    setCart((prevCart) =>
      prevCart.filter(
        (item) =>
          !(
            item.product._id === productId &&
            normalizeSelectedSize(item.selectedSize) === normalizedSelectedSize
          )
      )
    );
  };

  // Update item quantity
  const updateQuantity = (productId, quantity, selectedSize = '') => {
    const normalizedSelectedSize = normalizeSelectedSize(selectedSize);

    if (quantity <= 0) {
      removeFromCart(productId, normalizedSelectedSize);
      return;
    }

    setCart((prevCart) =>
      prevCart.map((item) =>
        item.product._id === productId &&
        normalizeSelectedSize(item.selectedSize) === normalizedSelectedSize
          ? { ...item, quantity }
          : item
      )
    );
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
  };

  // Calculate total price
  const getTotalPrice = () => {
    return cart.reduce(
      (total, item) => total + item.product.offerPrice * item.quantity,
      0
    );
  };

  // Get total number of items in cart
  const getCartCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  // Check if product is in cart
  const isInCart = (productId, selectedSize = null) => {
    if (selectedSize === null) {
      return cart.some((item) => item.product._id === productId);
    }

    const normalizedSelectedSize = normalizeSelectedSize(selectedSize);
    return cart.some(
      (item) =>
        item.product._id === productId &&
        normalizeSelectedSize(item.selectedSize) === normalizedSelectedSize
    );
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalPrice,
        getCartCount,
        isInCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
